import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormControl, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { exist } from '@campaign-portal/namespace/common/id';
import { RPCResult } from '@campaign-portal/namespace/common/rpc.response';
import { Campaign, CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import {
	AlarisDialogComponent,
	AlarisDialogService,
	AlarisIconDirective,
	AlarisNoValueCheckPipe,
	AlarisTextAreaComponent,
	DateFormatPipe,
	InputError,
} from '@campaign-portal/components-library';
import { CampaignsService } from '../../services/campaigns.service';
import { CanDeactivateComponent } from '@helpers/shared/can-deactivate/component-deactivate';
import { CanDeactivateGuardService } from '@helpers/shared/can-deactivate/can-deactivate.guard';

export interface CampaignsDialogData {
	type: CampaignsDialogType;
	campaign: Campaign<exist>;
	total?: number;
}

export type CampaignsDialogType = 'Delete' | 'Confirm';

@Component({
	selector: 'app-campaigns-dialog',
	templateUrl: './campaigns-dialog.component.html',
	styleUrls: ['../../../../../styles/dialog.scss', './shared-dialog.component.scss'],
	providers: [DatePipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslateModule,
		AlarisTextAreaComponent,
		AlarisDialogComponent,
		AlarisNoValueCheckPipe,
		AlarisIconDirective,
		DateFormatPipe,
	],
	standalone: true,
})
export class CampaignsDialogComponent extends CanDeactivateComponent implements OnDestroy {
	readonly campaignStatus = CampaignStatus;
	campaign!: Campaign<exist>;
	description: FormControl;
	total = 0;

	readonly errors: InputError[] = [{ key: 'maxlength', value: 'errors.maxCharacters', params: { amount: '170' } }];

	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly allowedDeactivation = new BehaviorSubject<boolean>(true);

	constructor(
		@Inject(DIALOG_DATA) public readonly data: CampaignsDialogData,
		private readonly dialogRef: DialogRef<unknown>,
		private readonly campaignsService: CampaignsService,
		private readonly translate: TranslateService,
		private readonly datePipe: DatePipe,
		private readonly cd: ChangeDetectorRef,
		private readonly dialog: AlarisDialogService,
		private readonly guard: CanDeactivateGuardService
	) {
		super();
		this.addDialogGuard(this.dialog, dialogRef, this.guard);
		this.campaign = data.campaign;
		this.description = new FormControl(this.campaign.info?.description || '', Validators.maxLength(170));
		this.total = data?.total || 0;
	}

	get disabled(): boolean {
		return this.loading$.getValue();
	}

	ngOnDestroy(): void {
		this.loading$.complete();
	}

	close(): void {
		this.dialogRef.close(false);
	}

	confirm(): void {
		this.loading$.next(true);
		let caller: Observable<RPCResult<unknown>> = of({ Success: false });

		switch (this.data.type) {
			case 'Delete':
				caller = this.campaignsService.delete(this.campaign);
				break;
			case 'Confirm':
				this.campaign.info.description = this.description.value ?? '';
				this.campaign.info.name = this.campaign.info.name
					? this.campaign.info.name
					: this.translate.instant('campaigns.campaign') +
						' ' +
						this.datePipe.transform(this.campaign.info.scheduled, 'd MMM y, HH:mm');
				caller = this.campaignsService.create(this.campaign);
				break;
			default:
				break;
		}

		this.allowedDeactivation.next(false);
		caller.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
			this.allowedDeactivation.next(true);
			this.loading$.next(false);
			this.dialogRef.close(resp.Success);
			this.cd.markForCheck();
		});
	}
}
